/**
 * Service Dependencies Page
 *
 * Displays dependency graph visualization and management UI
 */

import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useService, useServiceDependencies, useServiceDependents } from '../../graphql/queries';
import DependencyGraph from '../../components/services/DependencyGraph';
import { DependencyForm } from '../../components/services/DependencyForm';
import { DeleteConfirmationDialog } from '../../components/shared';
import { LoadingSpinner } from '../../components/shared/LoadingSpinner';
import { EmptyState } from '../../components/shared/EmptyState';
import {
  createDependency,
  updateDependency,
  deleteDependency,
  ApiError,
  type CreateDependencyInput,
  type UpdateDependencyInput,
} from '../../lib/apiClient';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './$serviceId.dependencies.css';

export const Route = createFileRoute('/services/$serviceId/dependencies')({
  component: ServiceDependenciesPage,
});

function ServiceDependenciesPage(): JSX.Element {
  const ForgeButton = 'forge-button' as React.ElementType;
  const { serviceId } = Route.useParams();

  // Fetch service data
  const { data: serviceData, loading: serviceLoading, error: serviceError } = useService(serviceId);

  // Fetch dependencies
  const {
    data: dependenciesData,
    loading: dependenciesLoading,
    error: dependenciesError,
    refetch: refetchDependencies,
  } = useServiceDependencies(serviceId);

  // Fetch dependents
  const {
    data: dependentsData,
    loading: dependentsLoading,
    error: dependentsError,
    refetch: refetchDependents,
  } = useServiceDependents(serviceId);

  // UI state
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingDependency, setEditingDependency] = useState<{
    toServiceId: string;
    dependencyType: string;
    communicationPattern: string;
    criticality?: string | null;
    notes?: string | null;
  } | null>(null);
  const [deletingDependency, setDeletingDependency] = useState<{
    fromServiceId: string;
    toServiceId: string;
    serviceName: string;
  } | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loading = serviceLoading || dependenciesLoading || dependentsLoading;
  const error = serviceError || dependenciesError || dependentsError;

  // Loading state
  if (loading && !serviceData) {
    return (
      <div className="service-dependencies-page">
        <LoadingSpinner message="Loading service dependencies..." />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="service-dependencies-page">
        <EmptyState
          title="Failed to load service"
          description={(error as Error).message || 'An error occurred while loading the service'}
          icon="error"
        />
      </div>
    );
  }

  // Service not found
  if (!serviceData?.service) {
    return (
      <div className="service-dependencies-page">
        <EmptyState
          title="Service not found"
          description={`Service with ID ${serviceId} does not exist`}
          icon="error"
        />
      </div>
    );
  }

  const service = serviceData.service;
  const dependencies = dependenciesData?.serviceDependencies || [];
  const dependents = dependentsData?.serviceDependents || [];

  // Type assertion for error message
  const errorMessage = error ? (error as Error).message || String(error) : undefined;

  // Handler for adding dependency
  const handleAddDependency = async (
    data: CreateDependencyInput | UpdateDependencyInput
  ): Promise<void> => {
    setFormLoading(true);
    setFormError(null);

    try {
      await createDependency(data as CreateDependencyInput);

      // Refetch both dependencies and dependents
      await Promise.all([refetchDependencies(), refetchDependents()]);

      setShowAddForm(false);
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message);
      } else {
        setFormError('Failed to create dependency. Please try again.');
      }
    } finally {
      setFormLoading(false);
    }
  };

  // Handler for editing dependency
  const handleEditDependency = async (
    data: CreateDependencyInput | UpdateDependencyInput
  ): Promise<void> => {
    if (!editingDependency) return;

    setFormLoading(true);
    setFormError(null);

    try {
      await updateDependency(
        serviceId,
        editingDependency.toServiceId,
        data as UpdateDependencyInput
      );

      // Refetch both dependencies and dependents
      await Promise.all([refetchDependencies(), refetchDependents()]);

      setEditingDependency(null);
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message);
      } else {
        setFormError('Failed to update dependency. Please try again.');
      }
    } finally {
      setFormLoading(false);
    }
  };

  // Handler for deleting dependency
  const handleDeleteDependency = async (): Promise<void> => {
    if (!deletingDependency) return;

    setDeleteLoading(true);

    try {
      await deleteDependency(deletingDependency.fromServiceId, deletingDependency.toServiceId);

      // Refetch both dependencies and dependents
      await Promise.all([refetchDependencies(), refetchDependents()]);

      setDeletingDependency(null);
    } catch (err) {
      // Handle error - could show a toast notification
      console.error('Failed to delete dependency:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="service-dependencies-page">
      {/* Header */}
      <div className="dependencies-header">
        <div className="dependencies-breadcrumb">
          <Link
            to="/services/$serviceId"
            params={{ serviceId }}
            className="dependencies-breadcrumb-link"
          >
            ← Back to Service
          </Link>
        </div>
        <div className="dependencies-title-section">
          <div className="dependencies-title-content">
            <h1>{service.name} - Dependencies</h1>
            <p className="dependencies-subtitle">
              Visualize and manage dependencies for this service
            </p>
          </div>
          <ForgeButton type="button" variant="raised" onClick={() => setShowAddForm(true)}>
            <forge-icon name="add" slot="start" aria-hidden="true"></forge-icon>
            Add Dependency
          </ForgeButton>
        </div>
      </div>

      {/* Add Dependency Form */}
      {showAddForm && (
        <div className="dependencies-form-wrapper">
          <DependencyForm
            currentServiceId={serviceId}
            excludeServiceIds={dependencies.map((dep) => dep.toServiceId)}
            onSubmit={handleAddDependency}
            onCancel={() => {
              setShowAddForm(false);
              setFormError(null);
            }}
            loading={formLoading}
            error={formError}
          />
        </div>
      )}

      {/* Edit Dependency Form */}
      {editingDependency && (
        <div className="dependencies-form-wrapper">
          <DependencyForm
            currentServiceId={serviceId}
            existingDependency={editingDependency}
            onSubmit={handleEditDependency}
            onCancel={() => {
              setEditingDependency(null);
              setFormError(null);
            }}
            loading={formLoading}
            error={formError}
          />
        </div>
      )}

      {/* Statistics */}
      <div className="dependencies-stats">
        <div className="stat-card">
          <div className="stat-card-label">Dependencies</div>
          <div className="stat-card-value">{dependencies.length}</div>
          <div className="stat-card-description">Services this service depends on</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Dependents</div>
          <div className="stat-card-value">{dependents.length}</div>
          <div className="stat-card-description">Services that depend on this service</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Total Connections</div>
          <div className="stat-card-value">{dependencies.length + dependents.length}</div>
          <div className="stat-card-description">All dependency relationships</div>
        </div>
      </div>

      {/* Dependency Graph */}
      <div className="dependencies-graph-wrapper">
        <DependencyGraph
          service={service}
          dependencies={dependencies}
          dependents={dependents}
          loading={loading}
          error={errorMessage}
        />
      </div>

      {/* Dependency Lists */}
      <div className="dependency-lists-grid">
        {/* Dependencies List */}
        <div className="dependency-list-section">
          <h2>Dependencies ({dependencies.length})</h2>
          {dependencies.length === 0 ? (
            <p className="dependency-list-empty">No dependencies</p>
          ) : (
            <div className="dependency-list-items">
              {dependencies.map((dep) => {
                if (!dep.toService) return null;
                return (
                  <div key={dep.toServiceId} className="dependency-item">
                    <div className="dependency-item-content">
                      <div className="dependency-item-info">
                        <Link
                          to="/services/$serviceId"
                          params={{ serviceId: dep.toServiceId }}
                          className="dependency-item-name"
                        >
                          {dep.toService.name}
                        </Link>
                        <div className="dependency-item-type">
                          {dep.toService.serviceType} • {dep.toService.tier}
                        </div>
                        <div className="dependency-item-metadata">
                          {dep.metadata.communicationPattern} • {dep.metadata.dependencyType}
                          {dep.metadata.criticality && ` • ${dep.metadata.criticality}`}
                        </div>
                        {dep.metadata.notes && (
                          <div className="dependency-item-notes">{dep.metadata.notes}</div>
                        )}
                      </div>
                      <div className="dependency-item-actions">
                        <ForgeButton
                          type="button"
                          variant="outlined"
                          onClick={() =>
                            setEditingDependency({
                              toServiceId: dep.toServiceId,
                              dependencyType: dep.metadata.dependencyType,
                              communicationPattern: dep.metadata.communicationPattern,
                              criticality: dep.metadata.criticality,
                              notes: dep.metadata.notes,
                            })
                          }
                          title="Edit dependency"
                        >
                          <forge-icon name="edit" aria-hidden="true"></forge-icon>
                          <span>Edit</span>
                        </ForgeButton>
                        <ForgeButton
                          type="button"
                          variant="outlined"
                          onClick={() =>
                            setDeletingDependency({
                              fromServiceId: serviceId,
                              toServiceId: dep.toServiceId,
                              serviceName: dep.toService.name,
                            })
                          }
                          title="Delete dependency"
                        >
                          <forge-icon name="delete" aria-hidden="true"></forge-icon>
                          <span>Delete</span>
                        </ForgeButton>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Dependents List */}
        <div className="dependency-list-section">
          <h2>Dependents ({dependents.length})</h2>
          {dependents.length === 0 ? (
            <p className="dependency-list-empty">No dependents</p>
          ) : (
            <div className="dependency-list-items">
              {dependents.map((dep) => {
                if (!dep.fromService) return null;
                return (
                  <div key={dep.fromServiceId} className="dependency-item">
                    <div className="dependency-item-content">
                      <div className="dependency-item-info">
                        <Link
                          to="/services/$serviceId"
                          params={{ serviceId: dep.fromServiceId }}
                          className="dependency-item-name"
                        >
                          {dep.fromService.name}
                        </Link>
                        <div className="dependency-item-type">
                          {dep.fromService.serviceType} • {dep.fromService.tier}
                        </div>
                        <div className="dependency-item-metadata">
                          {dep.metadata.communicationPattern} • {dep.metadata.dependencyType}
                          {dep.metadata.criticality && ` • ${dep.metadata.criticality}`}
                        </div>
                        {dep.metadata.notes && (
                          <div className="dependency-item-notes">{dep.metadata.notes}</div>
                        )}
                      </div>
                      <div className="dependency-item-actions">
                        <ForgeButton
                          type="button"
                          variant="outlined"
                          onClick={() =>
                            setDeletingDependency({
                              fromServiceId: dep.fromServiceId,
                              toServiceId: serviceId,
                              serviceName: dep.fromService.name,
                            })
                          }
                          title="Delete dependency"
                        >
                          <forge-icon name="delete" aria-hidden="true"></forge-icon>
                          <span>Delete</span>
                        </ForgeButton>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deletingDependency && (
        <DeleteConfirmationDialog
          open={true}
          title="Delete Dependency"
          message="Are you sure you want to delete this dependency?"
          itemName={`Dependency to ${deletingDependency.serviceName}`}
          warning="This action cannot be undone."
          onConfirm={handleDeleteDependency}
          onCancel={() => setDeletingDependency(null)}
          isDeleting={deleteLoading}
        />
      )}
    </div>
  );
}
